import { useTranslation } from "react-i18next";

import { asAiSignals, type AiSignal } from "@/entities/robot";
import { Icon, Panel } from "@/shared/ui";
import type { IconName } from "@/shared/ui";

/**
 * «Что анализирует AI» (ТЗ §13).
 *
 * Набор различается от робота к роботу — в этом весь смысл блока и весь ответ
 * на §4 «почему дорогой дороже». Anchor показывает две карточки, Pulse —
 * одиннадцать, и лестница видна сравнением экранов, а не обещанием в тексте.
 * Показать дешёвому роботу сигналы, которых он не использует, значит сломать
 * ровно то, ради чего блок написан; запрет на несовместимые пары (ликвидации у
 * спота) держит валидатор админки.
 *
 * Пустой набор блок не рисует: оператор заводит робота постепенно, и заголовок
 * над пустой сеткой выглядел бы поломкой.
 */
const ICONS: Record<AiSignal, IconName> = {
  price_action: "chartLineUp",
  volume: "trayFill",
  momentum: "pulse",
  volatility: "trendUp",
  market_structure: "shieldCheck",
  correlation: "shareNetwork",
  btc_dominance: "coin",
  market_sentiment: "eye",
  open_interest: "coinStack",
  funding_rate: "arrowCircleUp",
  liquidations: "warningFill",
};

export function AiSignalsGrid({ signals, core }: { signals: string[]; core: boolean }) {
  const { t } = useTranslation();
  const known = asAiSignals(signals);
  if (known.length === 0) return null;

  return (
    <Panel as="section" tone="violet" radius="lg" className="flex flex-col gap-3 p-4">
      <header className="flex flex-col gap-0.5">
        <h2 className="text-body font-semibold text-text-primary">
          {t("robots.analytics.ai.title")}
        </h2>
        <p className="text-micro font-normal text-text-tertiary">
          {/* «ZN AI Core» — ярлык флагмана из ТЗ §3, он появляется только у
              робота с полным набором источников. */}
          {core ? t("robots.analytics.ai.core") : t("robots.analytics.ai.subtitle")}
        </p>
      </header>

      <ul className="grid grid-cols-2 gap-2">
        {known.map((signal) => (
          <li
            key={signal}
            className="flex min-w-0 items-center gap-2 rounded-md bg-background-raised p-2.5"
          >
            <Icon name={ICONS[signal]} size={14} />
            <span className="truncate text-micro font-semibold text-text-primary">
              {t(`robots.analytics.ai.signal.${signal}`)}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
